import { useState } from "react";
import { Button } from "@/components/ui";
import {
  claudeProfilesText,
  turnOffClaudeShortcut,
  turnOffFailedText,
} from "@/desktop/claude-shortcut-turn-off";
import { useT } from "@/i18n";
import { settingsShortcutsMessages } from "@/i18n/messages/settings-shortcuts";
import type { ClaudeShortcutClash } from "@/lib/claude-app-shortcuts";

/**
 * Under a shortcut row the Claude app shares: a button that turns it off in
 * Claude (restarting Claude when it is open), and, for a changeable
 * shortcut, the way to pick another key for Denker.
 */
export function ClaudeShortcutClashNotice({
  clash,
  onPickKey,
}: {
  clash: ClaudeShortcutClash;
  onPickKey?: () => void;
}) {
  const t = useT(settingsShortcutsMessages);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  return (
    <>
      <p
        data-testid={`claude-clash-${clash.target}`}
        className="w-full text-appkit-small text-muted"
      >
        {t("claudeClashRow", {
          profiles: claudeProfilesText(clash.claude.profiles),
        })}
      </p>
      <Button
        variant="secondary"
        size="sm"
        disabled={busy}
        data-testid={`claude-clash-off-${clash.target}`}
        onClick={async () => {
          setFailure(null);
          setBusy(true);
          try {
            const { result } = await turnOffClaudeShortcut(clash, false);
            if (result !== "turnedOff" && result !== "restarted")
              setFailure(turnOffFailedText(clash, result));
          } catch {
            setFailure(turnOffFailedText(clash, "error"));
          } finally {
            setBusy(false);
          }
        }}
      >
        {t("turnOffInClaude")}
      </Button>
      {onPickKey ? (
        <Button
          variant="ghost"
          size="sm"
          className="text-muted"
          data-testid={`claude-clash-pick-${clash.target}`}
          onClick={onPickKey}
        >
          {t("useAnotherKey")}
        </Button>
      ) : null}
      {failure ? (
        <p role="alert" className="w-full text-appkit-small text-danger">
          {failure}
        </p>
      ) : null}
    </>
  );
}
